import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./CustomerAcquisition.css";
import { 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Database,
  Cpu,
  Target,
  TrendingUp,
  Phone,
  CheckCircle2,
  DollarSign,
  MousePointer,
  UserCheck,
  Headphones,
  BarChart3,
  Users,
  Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";


// ============================================
// CUSTOMER ACQUISITION VISUALIZATIONS
// ============================================

// Hero Stats Visualization
const HeroStatsVisualization = () => {
  const stats = [
    { value: '1M+', label: 'Daily Consumer Reach', color: '#3b82f6' },
    { value: '245M+', label: 'Opt-in Database', color: '#2563eb' },
    { value: '54%', label: 'Avg. Conversion Lift', color: '#1d4ed8' },
  ];

  return (
    <div className="ca-viz-container hero-stats-viz">
      <div className="ca-viz-header">
        <span className="ca-viz-label">ACQUISITION ENGINE</span>
        <span className="ca-viz-badge green">ACTIVE</span>
      </div>
      
      <div className="hero-stats-grid">
        {stats.map((stat, i) => (
          <div key={i} className="hero-stat-card" style={{ borderLeftColor: stat.color }}>
            <span className="hero-stat-value">{stat.value}</span>
            <span className="hero-stat-label">{stat.label}</span>
          </div>
        ))}
      </div>
      
      <div className="hero-viz-flow">
        <div className="flow-node">
          <Database size={16} />
          <span>Data</span>
        </div>
        <div className="flow-arrow">→</div>
        <div className="flow-node active">
          <Cpu size={16} />
          <span>IRIS</span>
        </div>
        <div className="flow-arrow">→</div>
        <div className="flow-node">
          <Target size={16} />
          <span>Activation</span>
        </div>
      </div>
    </div>
  );
};

// Response Method Visualization
const ResponseVisualization = () => {
  const channels = [
    { name: 'Websites', value: '1M+', desc: 'daily visitors' },
    { name: 'Email', value: '245M+', desc: 'opt-in database' },
    { name: 'Push', value: '89M+', desc: 'subscribers' },
    { name: 'SMS', value: '45M+', desc: 'opted-in' },
  ];

  return (
    <div className="ca-viz-container response-viz">
      <div className="ca-viz-header">
        <span className="ca-viz-label">RESPONSE CHANNELS</span>
        <span className="ca-viz-badge">MULTI-TOUCH</span>
      </div>
      
      <div className="response-channels">
        {channels.map((ch, i) => (
          <div key={i} className="response-channel">
            <div className="response-channel-value">{ch.value}</div>
            <div className="response-channel-info">
              <span className="channel-name">{ch.name}</span>
              <span className="channel-desc">{ch.desc}</span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="response-footer">
        <div className="response-metric">
          <MousePointer size={14} />
          <span>High-intent traffic to your funnels</span>
        </div>
      </div>
    </div>
  );
};

// Data Acquisition Visualization
const DataVisualization = () => {
  const dataTypes = [
    { type: 'Email Opt-ins', volume: '2.4M/month', quality: 98 },
    { type: 'SMS Opt-ins', volume: '890K/month', quality: 96 },
    { type: 'Phone Verified', volume: '1.2M/month', quality: 99 },
    { type: 'Intent Signals', volume: '5.6M/month', quality: 94 },
  ];

  return (
    <div className="ca-viz-container data-viz">
      <div className="ca-viz-header">
        <span className="ca-viz-label">FIRST-PARTY DATA</span>
        <span className="ca-viz-badge green">PERMISSION-BASED</span>
      </div>
      
      <div className="data-types">
        {dataTypes.map((d, i) => (
          <div key={i} className="data-type-row">
            <div className="data-type-info">
              <span className="data-type-name">{d.type}</span>
              <span className="data-type-volume">{d.volume}</span>
            </div>
            <div className="data-quality">
              <div className="quality-bar">
                <div className="quality-fill" style={{ width: `${d.quality}%` }}></div>
              </div>
              <span className="quality-value">{d.quality}%</span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="data-footer">
        <Database size={14} />
        <span>Direct integration into your marketing stack</span>
      </div>
    </div>
  );
};

// Voice Solution Visualization
const VoiceVisualization = () => {
  const voiceMetrics = [
    { label: 'Live Agents', value: '500+', status: 'active' },
    { label: 'Calls/Day', value: '125K', status: 'active' },
    { label: 'Connect Rate', value: '34.2%', status: 'high' },
    { label: 'Conv. Rate', value: '12.8%', status: 'high' },
  ];

  return (
    <div className="ca-viz-container voice-viz">
      <div className="ca-viz-header">
        <span className="ca-viz-label">VOICE SOLUTIONS</span>
        <span className="ca-viz-badge">HIGH-TOUCH</span>
      </div>
      
      <div className="voice-metrics">
        {voiceMetrics.map((m, i) => (
          <div key={i} className="voice-metric-card">
            <span className="voice-metric-value">{m.value}</span>
            <span className="voice-metric-label">{m.label}</span>
            <span className={`voice-status ${m.status}`}></span>
          </div>
        ))}
      </div>
      
      <div className="voice-features">
        <div className="voice-feature">
          <Headphones size={14} />
          <span>Inbound & Outbound</span>
        </div>
        <div className="voice-feature">
          <Phone size={14} />
          <span>IVR & Live Transfer</span>
        </div>
      </div>
    </div>
  );
};

// ROI Visualization
const ROIVisualization = () => {
  const metrics = [
    { label: 'Cost Per Acquisition', before: '$48.50', after: '$32.99', change: '-32%' },
    { label: 'Conversion Rate', before: '1.8%', after: '2.7%', change: '+50%' },
    { label: 'Customer LTV', before: '$340', after: '$425', change: '+25%' },
  ];

  return (
    <div className="ca-viz-container roi-viz">
      <div className="ca-viz-header">
        <span className="ca-viz-label">CAMPAIGN PERFORMANCE</span>
        <span className="ca-viz-badge green">+3.5x ROI</span>
      </div>
      
      <div className="roi-comparison">
        <div className="roi-header">
          <span></span>
          <span>Before</span>
          <span>After</span>
          <span>Impact</span>
        </div>
        {metrics.map((m, i) => (
          <div key={i} className="roi-row">
            <span className="roi-label">{m.label}</span>
            <span className="roi-before">{m.before}</span>
            <span className="roi-after">{m.after}</span>
            <span className={`roi-change ${m.change.startsWith('+') ? 'positive' : 'negative'}`}>
              {m.change}
            </span>
          </div>
        ))}
      </div>
      
      <div className="roi-summary">
        <div className="roi-total">
          <BarChart3 size={16} />
          <span>Average client sees 3.5x ROI within 90 days</span>
        </div>
      </div>
    </div>
  );
};

// Hero Section
const CAHero = () => {
  return (
    <section className="ca-hero" data-testid="ca-hero">
      <div className="ca-hero-content">
        <div className="ca-hero-text">
          <span className="ca-label">CUSTOMER ACQUISITION</span>
          <h1>Customer Acquisition. Redefined.</h1>
          <p>
            Reaching millions of consumers daily, IRIS powers high-impact acquisition 
            solutions tailored to your goals. Whether you're acquiring first-party data, 
            driving consumers into marketing funnels, or connecting qualified customers 
            to your team—we deliver measurable results.
          </p>
          <Button 
            className="btn-primary-zeta"
            onClick={() => document.getElementById('ca-cta')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Explore Solutions
            <ArrowRight size={18} />
          </Button>
        </div>
        <div className="ca-hero-visual">
          <HeroStatsVisualization />
        </div>
      </div>
    </section>
  );
};

// Acquisition Methods Section Header
const CAMethodsHeader = () => {
  return (
    <section className="ca-methods-header" id="ca-methods">
      <div className="ca-methods-header-content">
        <span className="ca-section-label">ACQUISITION METHODS</span>
        <h2>Three Proven Pathways to Growth</h2>
        <p>
          Our platform enables multiple acquisition strategies, each powered by 
          proprietary data and AI-driven optimization. Select the approach that 
          aligns with your business objectives.
        </p>
      </div>
    </section>
  );
};

// Method Card Component - Response
const ResponseMethod = () => {
  return (
    <section className="ca-method-section" data-testid="ca-method-response">
      <div className="ca-method-wrapper">
        <div className="ca-method-content">
          <div className="ca-method-number">01</div>
          <span className="ca-method-label">RESPONSE</span>
          <h2>Drive Qualified Consumer Actions</h2>
          <p className="ca-method-intro">
            Generate high-intent traffic to your websites, landing pages, lead generation 
            forms, and conversion funnels with precision-targeted campaigns.
          </p>
          
          <div className="ca-method-details">
            <p>
              Our curated digital properties reach over 1 million consumers daily. Combined with 
              one of the largest opt-in email databases in the country, plus push notifications 
              and SMS capabilities, we connect you with audiences primed to respond.
            </p>
          </div>
          
          <div className="ca-method-bullets">
            <div className="ca-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Drive qualified traffic to landing pages and lead forms</p>
            </div>
            <div className="ca-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Multi-channel reach: email, push, SMS, display</p>
            </div>
            <div className="ca-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>AI-optimized targeting for maximum conversion rates</p>
            </div>
          </div>
          
          <Button 
            className="btn-secondary-zeta"
            onClick={() => document.getElementById('ca-cta')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Learn More
            <ArrowRight size={16} />
          </Button>
        </div>
        <div className="ca-method-visual">
          <ResponseVisualization />
        </div>
      </div>
    </section>
  );
};

// Method Card Component - Data
const DataMethod = () => {
  return (
    <section className="ca-method-section reversed" data-testid="ca-method-data">
      <div className="ca-method-wrapper">
        <div className="ca-method-content">
          <div className="ca-method-number">02</div>
          <span className="ca-method-label">DATA</span>
          <h2>Acquire First-Party Data at Scale</h2>
          <p className="ca-method-intro">
            First-party data is the most valuable currency in digital marketing. 
            Grow your database with permission-based opt-ins from consumers who want 
            to hear from you.
          </p>
          
          <div className="ca-method-details">
            <p>
              Whether building your email marketing database or acquiring quality data 
              for custom audience targeting, millions of Americans opt-in daily to receive 
              communications from our advertising partners on a 1:1 basis.
            </p>
          </div>
          
          <div className="ca-method-bullets">
            <div className="ca-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>High-volume permission-based opt-ins</p>
            </div>
            <div className="ca-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Direct integration into your marketing funnel</p>
            </div>
            <div className="ca-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>TCPA-compliant data collection processes</p>
            </div>
          </div>
          
          <Button 
            className="btn-secondary-zeta"
            onClick={() => document.getElementById('ca-cta')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Learn More
            <ArrowRight size={16} />
          </Button>
        </div>
        <div className="ca-method-visual">
          <DataVisualization />
        </div>
      </div>
    </section>
  );
};

// Method Card Component - Voice
const VoiceMethod = () => {
  return (
    <section className="ca-method-section" data-testid="ca-method-voice">
      <div className="ca-method-wrapper">
        <div className="ca-method-content">
          <div className="ca-method-number">03</div>
          <span className="ca-method-label">VOICE</span>
          <h2>High-Touch for High-Value</h2>
          <p className="ca-method-intro">
            Complex products and services require human connection. Our voice solutions 
            enable direct conversations with qualified consumers at scale.
          </p>
          
          <div className="ca-method-details">
            <p>
              Voice solutions connect consumers directly to your team via IVR systems or 
              live agent transfers. Talk customers through product details, answer questions, 
              address concerns, and close high-value sales—all with precise targeting and 
              real-time optimization.
            </p>
          </div>
          
          <div className="ca-method-bullets">
            <div className="ca-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Inbound and outbound voice campaigns</p>
            </div>
            <div className="ca-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>IVR qualification and live agent transfers</p>
            </div>
            <div className="ca-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Ideal for insurance, finance, healthcare verticals</p>
            </div>
          </div>
          
          <Button 
            className="btn-secondary-zeta"
            onClick={() => document.getElementById('ca-cta')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Learn More
            <ArrowRight size={16} />
          </Button>
        </div>
        <div className="ca-method-visual">
          <VoiceVisualization />
        </div>
      </div>
    </section>
  );
};

// Results Section
const CAResults = () => {
  return (
    <section className="ca-results-section" data-testid="ca-results">
      <div className="ca-results-wrapper">
        <div className="ca-results-text">
          <span className="ca-section-label">MEASURABLE OUTCOMES</span>
          <h2>Results That Speak for Themselves</h2>
          <p>
            Our data-driven approach consistently delivers measurable improvements 
            in acquisition efficiency and ROI across industries.
          </p>
          
          <div className="ca-results-highlights">
            <div className="result-highlight">
              <TrendingUp size={20} />
              <div>
                <span className="highlight-value">54%</span>
                <span className="highlight-label">Average conversion lift</span>
              </div>
            </div>
            <div className="result-highlight">
              <DollarSign size={20} />
              <div>
                <span className="highlight-value">32%</span>
                <span className="highlight-label">CPA reduction</span>
              </div>
            </div>
            <div className="result-highlight">
              <UserCheck size={20} />
              <div>
                <span className="highlight-value">3.5x</span>
                <span className="highlight-label">Average ROI</span>
              </div>
            </div>
          </div>
        </div>
        <div className="ca-results-visual">
          <ROIVisualization />
        </div>
      </div>
    </section>
  );
};

// Case Studies Section
const CACaseStudies = () => {
  const cases = [
    {
      industry: 'Insurance',
      goal: 'Generate qualified leads for local insurance agents through mobile-first campaigns.',
      result: 'ROAS positive within 30 days. 32% lift in qualified leads.',
    },
    {
      industry: 'Ride-Share',
      goal: 'Acquire new drivers at scale while maintaining strict CPA thresholds.',
      result: 'Tens of thousands of new drivers acquired, 33% below CPA target.',
    },
    {
      industry: 'Telecom',
      goal: 'Drive high-consideration carrier switching decisions via direct consumer engagement.',
      result: 'Thousands of carrier switches through opt-in SMS campaigns.',
    },
  ];

  return (
    <section className="ca-cases-section" data-testid="ca-cases">
      <div className="ca-section-header">
        <span className="ca-section-label">CASE STUDIES</span>
        <h2>Proven Results Across Industries</h2>
      </div>
      
      <div className="ca-cases-grid">
        {cases.map((c, i) => (
          <div key={i} className="ca-case-card">
            <span className="case-industry">{c.industry}</span>
            <div className="case-goal">
              <strong>Objective:</strong> {c.goal}
            </div>
            <div className="case-result">
              <strong>Outcome:</strong> {c.result}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// FAQ Section
const CAFAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "How does IntentLogic identify high-value prospects?",
      answer: "We leverage our proprietary database of 245M+ consumer profiles enriched with behavioral, transactional, and intent signals. Our IRIS AI platform analyzes these signals to score and segment audiences based on their likelihood to convert for your specific offering."
    },
    {
      question: "What makes your customer acquisition approach different?",
      answer: "Unlike agencies that rely on third-party data, we own our data infrastructure. This means faster activation, higher match rates, and the ability to optimize campaigns based on real-time performance signals across our entire consumer graph."
    },
    {
      question: "How do you measure and optimize campaign performance?",
      answer: "We provide full-funnel attribution modeling with real-time dashboards. Our AI continuously optimizes targeting, creative, timing, and channel mix based on conversion patterns—reallocating budget toward the highest-performing segments automatically."
    },
    {
      question: "What industries do you specialize in?",
      answer: "We have deep experience in financial services, insurance, telecom, healthcare, and consumer goods. Our data infrastructure and methodology translate across verticals, with custom model development for specific business objectives."
    },
    {
      question: "How quickly can campaigns launch?",
      answer: "Most campaigns can be live within days, not weeks. Because we own our data and activation infrastructure, there's no waiting for third-party approvals or data syncs. We handle campaign setup, creative development, and optimization end-to-end."
    }
  ];

  return (
    <section className="ca-faq-section" data-testid="ca-faq">
      <h2>Frequently Asked Questions</h2>
      <div className="ca-faq-list">
        {faqs.map((faq, index) => (
          <div 
            key={index} 
            className={`ca-faq-item ${openIndex === index ? 'open' : ''}`}
          >
            <button 
              className="ca-faq-question"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <span>{faq.question}</span>
              {openIndex === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {openIndex === index && (
              <div className="ca-faq-answer">
                <p>{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// CTA Section
const CACTA = () => (
  <section className="ca-cta-section" id="ca-cta" data-testid="ca-cta">
    <div className="ca-cta-content">
      <h2>What Can We Help You Achieve?</h2>
      <p>
        Let us show you how leveraging our proprietary data, AI platform, and 
        custom campaigns can redefine your customer acquisition strategy.
      </p>
      <Button 
        className="btn-primary-zeta"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        Let's Connect
        <ArrowRight size={18} />
      </Button>
    </div>
  </section>
);

// Main Customer Acquisition Page Component
function CustomerAcquisitionPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <div className="ca-page" data-testid="customer-acquisition-page">
        <SEO 
          title="Customer Acquisition - Data-Driven Lead Generation"
          description="Acquire high-value customers with precision targeting powered by 245M+ consumer profiles. Our data-driven approach delivers measurable results and predictable growth."
          keywords="customer acquisition, lead generation, performance marketing, data-driven marketing, consumer targeting, digital advertising, acquisition marketing, conversion optimization"
          canonical="/customer-acquisition"
        />
        <CAHero />
        <CAMethodsHeader />
        <ResponseMethod />
        <DataMethod />
        <VoiceMethod />
        <CAResults />
        <CACaseStudies />
        <CAFAQ />
        <CACTA />
      </div>
    </Layout>
  );
}

export default CustomerAcquisitionPage;
